import { AddIcon, CloseIcon, DragHandleIcon } from "@chakra-ui/icons";
import {
  Box,
  GridItem,
  HStack,
  IconButton,
  SimpleGrid,
  VStack,
} from "@chakra-ui/react";
import moment from "moment-timezone";
import { FieldEditor } from "./FieldEditor";
// 🆕 Import DnD Kit
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Reuse your formatting function
export function formatItemPreviewValue(item, countryOptions = []) {
  const previewKeys = item?.PREVIEW?.PREVIEW_VALUE || [];
  const rawValues = previewKeys.map((key) => {
    const field = item[key];
    const value = field?.VALUE;
    if (!value) return "";
    if (field?.TYPE === "Dropdown" && Array.isArray(field.ENUM)) {
      const match = field.ENUM.find((entry) => entry.CODE === value);
      if (match) return match.TEXT;
    }
    if (field?.TYPE === "Country" && countryOptions.length > 0) {
      const match = countryOptions.find((option) => option.value === value);
      if (match) return match.label;
    }
    if (typeof value === "string") {
      const match = value.match(/\b\d{8}\b/);
      if (match) {
        const formattedDate = moment(match[0], "YYYYMMDD").format("YYYY-MM-DD");
        return value.replace(match[0], formattedDate);
      }
    }
    return value;
  });
  return rawValues.filter(Boolean).join(", ");
}

// 🆕 Sortable Field Component
function SortableField({
  id,
  subFieldKey,
  value,
  index,
  onArrayChange,
  tabKey,
  fieldKey,
  onDeleteItem,
  onEdit,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (!value) return null;
  const isTextBox = value.TYPE === "TextBox";

  return (
    <GridItem
      ref={setNodeRef}
      style={style}
      colSpan={isTextBox ? 4 : 1}
      position="relative"
    >
      <Box
        position="relative"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        p={2}
      >
        {/* Drag handle overlay */}
        <Box
          position="absolute"
          top={2}
          right="20%"
          {...attributes}
          {...listeners}
          cursor="grab"
          _active={{ cursor: "grabbing" }}
          zIndex={1}
          p={1}
          borderRadius="md"
          bg="white"
          _hover={{ bg: "gray.100" }}
        >
          <DragHandleIcon w={4} h={4} color="gray.500" />
        </Box>

        <FieldEditor
          label={value.MESSAGE}
          tooltip={value.TOOLTIP}
          code={value.REFERENCE?.CODE}
          referenceMap={value.REFERENCE}
          type={value.TYPE}
          value={value.VALUE}
          isEditable={value?.EDITABLE}
          onChange={(val) =>
            onArrayChange(tabKey, fieldKey, index, subFieldKey, val)
          }
          enumOptions={value.TYPE === "Dropdown" ? value.ENUM : undefined}
          MULTISELECT_TYPE={value.MULTISELECT_TYPE}
          onDeleteItem={() => onDeleteItem(index, subFieldKey)}
          onEdit={onEdit}
          isVisible={!("VISIBLE" in value) || value.VISIBLE !== false}
        />
      </Box>
    </GridItem>
  );
}

// Updated DataCardList with field-level drag and drop
export const DataCardList = ({
  data,
  tabKey,
  fieldKey,
  onArrayChange,
  onDeleteItem,
  onEdit,
  deleteArrayField,
  addRepeatableItem,
  onReorder, // 🆕 New prop to handle field reordering
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = (itemIndex) => (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const item = data[itemIndex];
    const fieldEntries = Object.entries(item).filter(
      ([key]) => key !== "PREVIEW"
    );

    const oldIndex = fieldEntries.findIndex(([key]) => key === active.id);
    const newIndex = fieldEntries.findIndex(([key]) => key === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder the fields
    const reorderedFields = arrayMove(fieldEntries, oldIndex, newIndex);

    // Reconstruct the item with reordered fields
    const newItem = {
      PREVIEW: item.PREVIEW,
      ...Object.fromEntries(reorderedFields),
    };

    // Call parent to update this specific item
    if (onReorder) {
      const newData = [...data];
      newData[itemIndex] = newItem;
      onReorder(newData);
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      {data.map((item, index) => {
        const fieldEntries = Object.entries(item);
        return (
          <Box
            key={index}
            borderWidth="1px"
            borderRadius="md"
            p={4}
            shadow="sm"
            bg="white"
          >
            <HStack justify="space-between" mb={2}>
              <Box fontWeight="bold">
                {item?.PREVIEW?.PREVIEW_VALUE?.join(", ")}
              </Box>
              <HStack spacing={2}>
                <IconButton
                  icon={<AddIcon />}
                  size="sm"
                  // variant="ghost"
                  aria-label="Add item"
                  onClick={() => addRepeatableItem(index)}
                />
                <IconButton
                  icon={<CloseIcon />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  aria-label="Delete item"
                  onClick={() => deleteArrayField(index)}
                />
              </HStack>
            </HStack>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd(index)}
            >
              <SortableContext
                items={fieldEntries.map(([key]) => key)}
                strategy={rectSortingStrategy}
              >
                <SimpleGrid columns={{ base: 1, md: 4 }} gap={4}>
                  {fieldEntries.map(([subFieldKey, value], idx) => (
                    <SortableField
                      key={subFieldKey}
                      id={subFieldKey}
                      subFieldKey={subFieldKey}
                      value={value}
                      index={index}
                      onArrayChange={onArrayChange}
                      tabKey={tabKey}
                      fieldKey={fieldKey}
                      onDeleteItem={onDeleteItem}
                      onEdit={() => onEdit(value, index, subFieldKey)}
                      idx={idx}
                    />
                  ))}
                </SimpleGrid>
              </SortableContext>
            </DndContext>
          </Box>
        );
      })}
    </VStack>
  );
};
